import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ui from '../../styles/ui.module.css';
import CopyButton from './CopyButton';
import QrCode from './QrCode';
import ShareOptions from './ShareOptions';
import styles from './Secret.module.css';

/*
 * The two cards for handing something over: the link, then (when there is
 * one) the passphrase by a different route. Used by the burn-link result
 * (SecretCreated) and the live handoff (LiveSendPage).
 */

function StepHead({ id, step, title, tone }: { id: string; step: number | null; title: string; tone: 'link' | 'passphrase' }) {
  return (
    <div className={styles.stepHead}>
      {step !== null && (
        <span className={tone === 'link' ? styles.stepNumber : styles.stepNumberPassphrase} aria-hidden="true">
          {step}
        </span>
      )}
      <h3 id={id} className={tone === 'link' ? styles.stepTitle : styles.stepTitlePassphrase}>
        {title}
      </h3>
    </div>
  );
}

/** The link alone. `text` is the message shared with it; it may mention a passphrase but never contains one. */
export function LinkStep({ link, step, text }: { link: string; step: number | null; text: string }) {
  const { t } = useTranslation();
  const [qr, setQr] = useState(false);
  const qrId = useId();
  return (
    <section className={styles.linkStep} aria-labelledby="link-step-title">
      <StepHead id="link-step-title" step={step} title={t('secret.created.linkStepTitle')} tone="link" />
      <output className={styles.linkBox} data-testid="secret-link" aria-label={t('secret.created.linkLabel')}>
        {link}
      </output>
      <CopyButton value={link} label={t('secret.created.copyLink')} className={ui.button} />
      <ShareOptions label={t('secret.share.linkLabel')} text={text} url={link} emailSubject={t('secret.share.subject')} showHint />
      <button type="button" className={styles.textButton} aria-expanded={qr} aria-controls={qrId} onClick={() => setQr(!qr)}>
        {t(qr ? 'secret.created.hideQr' : 'secret.created.showQr')}
      </button>
      {qr && (
        <div className={styles.qr} id={qrId}>
          <QrCode value={link} label={t('secret.created.qrLabel')} />
          <p className={ui.hint}>{t('secret.created.qrHint')}</p>
        </div>
      )}
    </section>
  );
}

/** The second channel: the passphrase on its own, never alongside the link. */
export function PassphraseStep({ passphrase }: { passphrase: string }) {
  const { t } = useTranslation();
  const [shown, setShown] = useState(false);
  // Appended, not interpolated: nothing in i18n should ever get a chance to rewrite it.
  const text = `${t('secret.share.passphraseIntro')} ${passphrase}`;
  return (
    <section className={styles.passphraseStep} aria-labelledby="passphrase-step-title">
      <StepHead id="passphrase-step-title" step={2} title={t('secret.created.passphraseStepTitle')} tone="passphrase" />
      <p className={styles.passphraseHint}>{t('secret.share.passphraseHint')}</p>
      <div className={styles.passphraseRow}>
        <output className={`${styles.linkBox} ${styles.passphraseBox}`} aria-label={t('secret.share.passphraseLabel')} data-testid="share-passphrase">
          {/* A fixed mask, so it doesn't give away the length either. */}
          {shown ? passphrase : '••••••••••••'}
        </output>
        <button
          type="button"
          className={styles.onTintButton}
          onClick={() => setShown(!shown)}
          aria-label={t(shown ? 'secret.share.hidePassphrase' : 'secret.share.showPassphrase')}
        >
          {t(shown ? 'secret.share.hide' : 'secret.share.show')}
        </button>
      </div>
      <CopyButton value={passphrase} label={t('secret.share.copyPassphrase')} className={styles.darkButton} />
      <ShareOptions label={t('secret.share.passphraseLabelGroup')} text={text} emailSubject={t('secret.share.passphraseSubject')} />
    </section>
  );
}
